import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { AUTH_URL } from '../constants/api.constants';
import { ConfigurationService } from './configuration.service';
import { AppTheme, LoginResponse } from '../models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY  = 'bb_token';
  private readonly USER_KEY   = 'bb_user';
  private readonly PIC_KEY    = 'bb_profile_pic';
  readonly THEME_KEY          = 'bb_theme';

  private _profilePic$ = new BehaviorSubject<string | null>(
    localStorage.getItem('bb_profile_pic')
  );
  readonly profilePic$ = this._profilePic$.asObservable();

  constructor(private http: HttpClient, private router: Router, private config: ConfigurationService) {}

  login(email: string, password: string, clientCode: string): Observable<LoginResponse> {
    return this.http.post<any>(`${this.config.apiBaseUrl}${AUTH_URL}/userLogin`, { criteria: { email, password, clientCode } }).pipe(
      map((res: any) => {
        if (res?.errorCode !== '000000' || !res?.Data?.token) {
          throw new Error(res?.errorDescription || 'Login failed. Please try again.');
        }
        this.storeSession(res.Data);
        return res.Data as LoginResponse;
      })
    );
  }

  storeSession(data: LoginResponse): void {
    localStorage.setItem(this.TOKEN_KEY, data.token);
    localStorage.setItem(this.USER_KEY, JSON.stringify({
      userID:               data.userID,
      email:                data.email,
      name:                 data.name,
      role:                 data.role,
      clientName:           data.clientName || '',
      subscriptionValidTill: data.subscriptionValidTill || null,
      quickAccessRoutes:    data.quickAccessRoutes || [],
      theme:                data.theme || 'brown',
    }));
    localStorage.setItem(this.THEME_KEY, data.theme || 'brown');
  }

  updateStoredQuickAccessRoutes(routes: string[]): void {
    const user = this.getCurrentUser();
    if (!user) return;
    user.quickAccessRoutes = routes;
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  updateStoredTheme(theme: AppTheme): void {
    const user = this.getCurrentUser();
    if (!user) return;
    user.theme = theme;
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    localStorage.setItem(this.THEME_KEY, theme);
  }

  getStoredTheme(): AppTheme {
    return (localStorage.getItem(this.THEME_KEY) as AppTheme) || 'brown';
  }

  getLicenseInfo(): { daysRemaining: number; validTill: Date } | null {
    const svt = this.getCurrentUser()?.subscriptionValidTill;
    if (!svt) return null;
    // Slice the YYYY-MM-DD portion directly from the ISO string before any JS timezone
    // conversion. "2026-05-31T18:30:00.000+00:00" → "2026-05-31" so a date stored as
    // midnight IST (18:30 UTC) is still treated as May 31, not June 1.
    const svtStr = typeof svt === 'string' ? svt : new Date(svt).toISOString();
    const validTillDateStr = svtStr.split('T')[0];
    const today = new Date();
    const todayDateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    // Parse both as UTC midnight so the subtraction is a clean whole-day integer.
    const validTill = new Date(validTillDateStr);
    const todayMidnight = new Date(todayDateStr);
    const daysRemaining = Math.round((validTill.getTime() - todayMidnight.getTime()) / 86400000);
    return { daysRemaining, validTill };
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getCurrentUser(): any {
    const user = localStorage.getItem(this.USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  getProfilePic(): string | null {
    return localStorage.getItem(this.PIC_KEY);
  }

  setProfilePic(base64: string): void {
    localStorage.setItem(this.PIC_KEY, base64);
    this._profilePic$.next(base64);
  }

  clearProfilePic(): void {
    localStorage.removeItem(this.PIC_KEY);
    this._profilePic$.next(null);
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem('bb_selected_center');
    localStorage.removeItem('bb_manager_centers');
    this.router.navigateByUrl('/login');
  }

  clearTheme(): void {
    localStorage.removeItem(this.THEME_KEY);
  }
}
